/**
 * Equity compensation options shared across the offer and job forms.
 */
export interface CompensationOption {
  label: string;
  value: string;
}

/**
 * Equity instruments, mirroring `AtsEquityAward#type_of_equity` 1:1. Used by the offer
 * form and the offer-field settings; jobs carry no equity instrument today.
 */
export const EQUITY_TYPE_OPTIONS: CompensationOption[] = [
  { label: 'Common Stock', value: 'common_stock' },
  { label: 'Preferred Stock', value: 'preferred_stock' },
  { label: 'Restricted Stock', value: 'restricted_stock' },
  { label: 'Stock option', value: 'stock_option' },
];

export type AwardType = 'units' | 'money';

/**
 * How an equity amount is expressed. On jobs this maps onto the `unit` column of
 * ats_job_salaries: `quantity` for a share count, `currency` for a money amount.
 */
export const AWARD_TYPE_OPTIONS: CompensationOption[] = [
  { value: 'units', label: 'Units' },
  { value: 'money', label: 'Money' },
];

export const AWARD_TYPE_UNITS: AwardType = 'units';
export const AWARD_TYPE_MONEY: AwardType = 'money';

/** ats_job_salaries.unit values corresponding to each award type. */
export const SALARY_UNIT_QUANTITY = 'quantity';
export const SALARY_UNIT_CURRENCY = 'currency';

/** Maps the FE award type onto the backend `unit` value. */
export const awardTypeToSalaryUnit = (awardType?: AwardType | string) =>
  awardType === AWARD_TYPE_MONEY ? SALARY_UNIT_CURRENCY : SALARY_UNIT_QUANTITY;

/** Maps a stored `unit` back onto the FE award type. */
export const salaryUnitToAwardType = (unit?: string | null): AwardType =>
  unit === SALARY_UNIT_CURRENCY ? AWARD_TYPE_MONEY : AWARD_TYPE_UNITS;

/**
 * The ISO short code (USD / INR), never the full currency name, so a selection
 * fits the compact currency select that sits beside an amount input. Prefers the
 * explicit `currency_code`; otherwise pulls the code out of a "US Dollar (USD)"
 * label.
 */
export const currencyShortCode = (currency: any): string => {
  if (!currency) return '';
  if (currency.currency_code) return currency.currency_code;
  const label = typeof currency.currency === 'string' ? currency.currency : '';
  const match = /\(([^)]+)\)/.exec(label);
  return match ? match[1] : label;
};

/** Currency options for a compact select: `{ label: 'USD', value: id }`. */
export const toCurrencyShortOptions = (currencies: any[] = []) =>
  currencies.map((currency) => ({
    label: currencyShortCode(currency),
    value: currency.id,
  }));

export const DEFAULT_CURRENCY_CODE = 'USD';

/**
 * USD's country id — the floor every compensation currency field falls back to
 * when nothing else resolves.
 *
 * Prefer `resolveDefaultCurrencyId` below, which finds USD by short code in the
 * loaded currency list rather than trusting a hardcoded id. This constant is the
 * last resort for callers that have no currency list to search (e.g. pure form
 * helpers), and names the `|| 5` that is otherwise scattered across the offer
 * and requisition forms.
 */
export const USD_CURRENCY_COUNTRY_ID = 5;

/**
 * The currency to preselect when a compensation field has none yet.
 *
 * A configured currency always wins — an office (or the account) set up for INR
 * must not be overridden. USD is only the fallback when nothing is configured,
 * and the first available currency is the last resort.
 *
 * Returns undefined until the currency list has loaded, so nothing is set before
 * a match can be made.
 */
export const resolveDefaultCurrencyId = (
  currencies: any[] = [],
  configuredId?: number | null
): number | undefined => {
  if (!currencies.length) return undefined;
  if (configuredId) return configuredId;

  const usd = currencies.find(
    (currency) => currencyShortCode(currency) === DEFAULT_CURRENCY_CODE
  );
  return usd?.id ?? currencies[0]?.id;
};
